/**
 * useElementSize.ts
 *
 * An element's rendered size, kept current as it resizes. For layouts that have
 * to answer to the space a component is actually given, not the window: the app
 * sidebar alone moves that by ~200px at the same viewport width.
 *
 * Measure an element with no padding or border of its own: the size read is its
 * border box.
 */
import { useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

export interface ElementSize {
  width: number;
  height: number;
}

/**
 * 0 × 0 until the element is first measured — and always in jsdom, which lays
 * nothing out — so treat 0 as "not measured", not as "no room". The first
 * measurement happens in a layout effect, before the browser paints.
 */
export function useElementSize(ref: RefObject<HTMLElement>): ElementSize {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const rect = el.getBoundingClientRect();
      const next = { width: Math.round(rect.width), height: Math.round(rect.height) };
      // Same size, same object: no re-render for a resize that changed nothing.
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}
